import { EventFeedEvent } from '@api';
import { Button } from '@design/components/button/Button';
import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { TabLines } from '@design/components/tab-lines/TabLines';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { useScrollListener } from '@helpers';
import { useQueryClient } from '@tanstack/react-query';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import {
  QUERY_KEY_EVENT_FEED,
  useEventFeed,
} from '@terminal/react-query-hooks/events/use-event-feed/useEventFeed';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { tabTypeToEventFeedType } from '@terminal/stores/use-feed-filters/useFeedFilters';
import {
  FollowingInsightFeedFilterTabs,
  followingTabKeys,
} from '@terminal/views/insight-feed/components/insight-feed-type-tab-line/InsightFeedTypeTabLine';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { CatalystRow } from './components/catalyst-row/CatalystRow';
import { EarningsRow } from './components/earnings-row/EarningsRow';
import { NoEvents } from './components/no-events/NoEvents';
import { ScenarioRow } from './components/scenario-row/ScenarioRow';
import styles from './OverviewEventFeed.module.scss';
import {
  formatDateHeader,
  getLocalDateKey,
  getStartDate,
  type OverviewEventFeedPeriod,
  PERIOD_TITLE_KEY,
} from './utils';

export const HEADER_AND_TABS_HEIGHT = 94;
export const END_OF_FEED_HEIGHT = 32;
export const CARD_ROW_HEIGHT = 166;
export const MAX_CARDS_HEIGHT = 474;

export interface OverviewEventFeedProps {
  entityTag: string;
  isDrawer?: boolean;
  period?: OverviewEventFeedPeriod;
  showViewAllButton?: boolean;
  source: PosthogTrackingSource;
}

export const OverviewEventFeed = ({
  entityTag,
  isDrawer = false,
  period = '1W',
  showViewAllButton = false,
  source,
}: OverviewEventFeedProps) => {
  const { t } = useTranslation([
    'whatsNew',
    'earnings',
    'insights',
    'stream',
    'calendar',
  ]);
  const [activeTab, setActiveTab] =
    useState<FollowingInsightFeedFilterTabs>('all');
  const [persistedAllTabCount, setPersistedAllTabCount] = useState(0);
  const navigate = useNavigate();
  const { trackInsightFeedOpen } = usePosthogEvents();
  const feedWrapperRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const payload = {
    entities: [entityTag],
    type: tabTypeToEventFeedType[activeTab],
    date_from: getStartDate(period).toISOString(),
    limit: 100,
  };

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useEventFeed(payload);

  const allEvents = useMemo(
    () => data?.pages?.flatMap(page => page.events) ?? [],
    [data?.pages]
  );

  useScrollListener(
    feedWrapperRef,
    () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }

      return hasNextPage;
    },
    [hasNextPage, isFetchingNextPage],
    { isGlobalScrollListened: false, offset: 100 }
  );

  //persist all tab count for drawer height
  useEffect(() => {
    if (isDrawer && activeTab === 'all' && !isLoading) {
      setPersistedAllTabCount(allEvents.length);
    }
  }, [isDrawer, activeTab, allEvents.length, isLoading]);

  const tabs: Record<FollowingInsightFeedFilterTabs, string> = {
    all: t('stream:insightFeedTypeTabs.all'),
    company_catalyst: t('stream:insightFeedTypeTabs.companyCatalysts'),
    scenarios: t('stream:insightFeedTypeTabs.scenario'),
    earnings: t('stream:insightFeedTypeTabs.earnings'),
  };

  const handleExpandClick = () => {
    trackInsightFeedOpen(source, { entities: [entityTag] });

    const searchParams = new URLSearchParams({
      [queryKeys.entityTag]: entityTag,
      [queryKeys.activeTab]: activeTab,
    }).toString();

    navigate(`${appPaths.insightFeed}/explore?${searchParams}`, {
      replace: true,
    });
  };

  const handleTabChange = (_index: number, tabKey: string) => {
    queryClient.removeQueries({
      queryKey: [QUERY_KEY_EVENT_FEED, payload],
      exact: false,
    });
    const nextTab = followingTabKeys.find(key => key === tabKey);

    if (nextTab) {
      setActiveTab(nextTab);
    }
  };

  const countForHeight = persistedAllTabCount;
  const cardCount = Math.max(countForHeight, 1);
  const cardsHeight =
    cardCount >= 3 ? MAX_CARDS_HEIGHT : cardCount * CARD_ROW_HEIGHT;

  const feedWrapperHeight = isDrawer
    ? cardsHeight + END_OF_FEED_HEIGHT
    : undefined;
  const containerHeight = isDrawer
    ? HEADER_AND_TABS_HEIGHT + (feedWrapperHeight ?? 0)
    : undefined;

  const renderEventRow = (event: EventFeedEvent, isLast: boolean) => {
    const props = {
      event,
      isLast,
    };

    switch (event.type) {
      case 'earnings-insight':
        return <EarningsRow {...props} />;
      case 'attribution-insight':
        return <CatalystRow {...props} />;
      case 'scenario-insight':
        return <ScenarioRow {...props} />;
      default:
        return null;
    }
  };

  const titleKey = PERIOD_TITLE_KEY[period].key;

  return (
    <section
      className={styles.container}
      style={
        containerHeight
          ? { maxHeight: containerHeight, height: containerHeight }
          : undefined
      }
    >
      <div className={styles.header}>
        <h3 className={styles.title}>
          {/* i18next-extract-mark-context-next-line  ["1D", "3D", "1W", "1M"] */}
          {t(titleKey, { count: PERIOD_TITLE_KEY[period].count })}
        </h3>
        <WebOnlyFeat>
          <div className={styles.buttonContainer}>
            {showViewAllButton ? (
              <Tooltip
                label={t('insights:viewMore')}
                inPortal
                trigger={[TooltipTrigger.Hover]}
              >
                <ButtonLink
                  label={t('whatsNew:viewOlderInsights')}
                  iconName="ChevronLightRight"
                  iconPosition="right"
                  onClick={handleExpandClick}
                  className={styles.expandedButtonLink}
                />
              </Tooltip>
            ) : (
              <Tooltip
                label={t('insights:viewMore')}
                inPortal
                trigger={[TooltipTrigger.Hover]}
              >
                <Button
                  variant="tertiary"
                  size="small"
                  iconName="Expand"
                  onClick={handleExpandClick}
                />
              </Tooltip>
            )}
          </div>
        </WebOnlyFeat>
      </div>
      <TabLines
        className={styles.styledTabLines}
        activeTab={activeTab}
        onChange={handleTabChange}
        tabs={followingTabKeys.map(key => ({
          tabKey: key,
          tabLabel: tabs[key],
        }))}
      />
      <div
        className={styles.feedWrapper}
        ref={feedWrapperRef}
        style={
          feedWrapperHeight
            ? { maxHeight: feedWrapperHeight, height: feedWrapperHeight }
            : undefined
        }
      >
        {isLoading ? (
          <div className={styles.styledLoaderWrapper}>
            <SkeletonLoader
              areas={[
                {
                  styles: {
                    width: '100%',
                    height: '200px',
                    borderRadius: '8px',
                  },
                },
              ]}
            />
          </div>
        ) : allEvents.length === 0 && !hasNextPage && !isFetchingNextPage ? (
          <NoEvents activeTab={activeTab} period={period} />
        ) : (
          <>
            {allEvents.map((event, index) => {
              const currentDateKey = getLocalDateKey(event.created_at);
              const prevDateKey =
                index > 0
                  ? getLocalDateKey(allEvents[index - 1].created_at)
                  : null;
              const showDateHeader = currentDateKey !== prevDateKey;
              const isLast = index === allEvents.length - 1 && !hasNextPage;

              return (
                <React.Fragment key={event.id}>
                  {showDateHeader && (
                    <div className={styles.dateHeader}>
                      <p className={styles.dateText}>
                        {formatDateHeader(event.created_at, t)}
                      </p>
                    </div>
                  )}
                  {renderEventRow(event, isLast)}
                </React.Fragment>
              );
            })}
            {hasNextPage && isFetchingNextPage && (
              <div className={styles.paginationLoaderWrapper}>
                <SkeletonLoader
                  areas={[
                    {
                      styles: {
                        width: '100%',
                        height: '60px',
                        borderRadius: '8px',
                        margin: '10px 0',
                      },
                    },
                  ]}
                />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};
